import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useForm } from "react-hook-form"
import { DELIVERY_OPTIONS } from "src/app/constants"
import useUpdateEmailAndShippingAddress from "src/app/hooks/useUpdateEmailAndShippingAddress"
import PickupScheduleCard from "./PickupScheduleCard"
import ZipScheduleCard from "./ZipScheduleCard"
import Addons from "../addons/Addons"
import BackAndNextButton from "../common/BackAndNextButton"
import EmailInput from "../common/EmailInput"
import PhoneInput from "../common/PhoneInput"
import TextInput from "../common/TextInput"
import HawaiiShippingRatesCard from "./HawaiiShippingRatesCard"
import { LoadingButton } from "@mui/lab"
import { useDraftOrderMutation } from "src/features/checkout/checkoutApi"
import { useEffect } from "react"
import { toast } from "react-toastify"
import InformationHeader from "./InformationHeader"
import { AccountCircle } from "@mui/icons-material"
import { hasBeerOrSeltzerItemsSelector, setCart } from "src/features/checkout/checkoutSlice"
import analytics from "src/app/analytics"

function BottomNav({ loading, deliveryOption }) {
  return (
    <BackAndNextButton
      loading={loading}
      // backButtonLabel="Return to options"
      backButtonPath="../options"
      nextButtonLabel={`Continue to ${
        deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY
          ? "delivery"
          : "shipping"
      }`}
      nextButtonType="submit"
    />
  )
}

export default function DeliveryOrShippingForm() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const customer = useSelector((state) => state.checkout.customer)
  const {cart, checkout} = useSelector((state) => state.checkout)
  const hasBeerItems = useSelector(hasBeerOrSeltzerItemsSelector)
  const { deliveryOption, zip, isAsrc, zipData } = useSelector(
    (state) => state.checkout.options,
  )

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: customer.email || cart.buyerIdentity?.email || "",
      company: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.company || "",
      firstName: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.firstName || "",
      lastName: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.lastName || "",
      address1: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.address1 || "",
      address2: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.address2 || "",
      city: zipData?.city || "",
      country: "United States",
      province: zipData?.province || "",
      phone: cart.buyerIdentity?.deliveryAddressPreferences?.[0]?.phone || "",
      zip: zip || "",
    },
  })

  const [
    updateEmailAndShippingAddress,
    {
      isLoading,
      data: updateEmailAndShippingAddressData,
    },
  ] = useUpdateEmailAndShippingAddress()

  const [
    draftOrder,
    {
      isLoading: isDraftOrderLoading,
      data: draftOrderData,
      error: draftOrderError,
    },
  ] = useDraftOrderMutation()

  const onSubmit = async ({ email, ...shippingAddress }) => {
    const { firstName, lastName, ...shipping_address } = shippingAddress

    analytics.setUserProperties({
      email,
    })

    analytics.track("add_shipping_info", {
      cart_id: cart.id,
      email,
      first_name: firstName,
      last_name: lastName,
      ...shipping_address,
    })

    await updateEmailAndShippingAddress({ email, shippingAddress })

    if ([DELIVERY_OPTIONS.US_MAINLAND_SHIPPING, DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING].includes(deliveryOption) && hasBeerItems) {
      const draftOrderParams = {
        email,
        shipping_address: {
          first_name: firstName,
          last_name: lastName,
          ...shipping_address,
        },
        billing_address: {
          first_name: firstName,
          last_name: lastName,
          ...shipping_address,
        },
        line_items: [
          ...cart.lines.edges.map(({ node }) => ({
            variant_id: node.merchandise.id.replace(
              "gid://shopify/ProductVariant/",
              "",
            ),
            quantity: node.quantity,
            applied_discount: node.discountAllocations.reduce(
              (discounts, discountAllocation) => {
                return {
                  title: discountAllocation.discountApplication.code,
                  value: discountAllocation.discountedAmount.amount,
                  value_type: "fixed_amount",
                  amount: discountAllocation.discountedAmount.amount,
                }
              },
              null,
            ),
          })),
        ],
      }
      if (deliveryOption === DELIVERY_OPTIONS.US_MAINLAND_SHIPPING) {
        draftOrderParams.line_items.push({
          variant_id: null,
          variant_title: "General Excise Tax (GET)",
          title: "General Excise Tax",
          quantity: 1,
          price: parseFloat(cart.cost.totalAmount.amount) * 0.04712,
          requires_shipping: false,
          taxable: false,
        })
      }
      draftOrder(draftOrderParams)
      return
    }

    navigate(
      deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY
        ? "../delivery"
        : "../shipping",
      {
        state: {
          previousDeliveryOption: deliveryOption,
        },
      },
    )
  }

  useEffect(() => {
    console.log('deliveryOrShippingForm updateEmailAndShippingAddressData', updateEmailAndShippingAddressData)
    if (updateEmailAndShippingAddressData?.cartBuyerIdentityUpdate?.cart) {
      dispatch(setCart(updateEmailAndShippingAddressData?.cartBuyerIdentityUpdate?.cart))
    }
  }, [updateEmailAndShippingAddressData])

  useEffect(() => {
    if (draftOrderError) {
      toast.error(
        "Something went wrong while trying to submit the form. Please try again in a while.",
      )
    }
  }, [draftOrderError])

  useEffect(() => {
    if (isAsrc) {
      setValue("address1", "99-1405 Koaha Place")
      setValue("address2", "Unit F2/3")
    }
  }, [isAsrc])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <InformationHeader />

      {[
        DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
        DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING
      ].includes(deliveryOption) && hasBeerItems && (
        <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
          Please complete below form, click submit and we will send you a
          shipping quote shortly.
        </Alert>
      )}

      {isAsrc && (
        <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
          For Wake Islander/ASRC orders, please use the ASRC Warehouse Address
          as the delivery address:
          <br />
          99-1405 Koaha Place Unit F2/3, Aiea HI 96701
        </Alert>
      )}

      <Grid container spacing={2} direction="row-reverse">
        <Grid
          item
          xs={12}
          lg={deliveryOption === DELIVERY_OPTIONS.US_MAINLAND_SHIPPING ||
             (deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING && hasBeerItems) ? 12 : 5}
        >
          {deliveryOption === DELIVERY_OPTIONS.LOCAL_DELIVERY && (
            <>
              <ZipScheduleCard />
              <PickupScheduleCard />
            </>
          )}
          {deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING && !hasBeerItems && (
            <HawaiiShippingRatesCard />
          )}
        </Grid>

        <Grid
          item
          xs={12}
          lg={deliveryOption === DELIVERY_OPTIONS.US_MAINLAND_SHIPPING ||
             (deliveryOption === DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING && hasBeerItems) ? 12 : 7}
        >
          {!customer?.id && (
            <>
              <EmailInput
                name="email"
                control={control}
                label="Purchaser's Email Address"
                requiredErrorMessage="Please enter purchaser's email address."
                size="small"
                fullWidth
                sx={{ mb: 0.5 }}
              />
              <Typography color="text.secondary" variant="subtitle1" mb={2}>
                (
                {[
                  DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
                  DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING
                ].includes(deliveryOption)
                  ? "We will email you shortly with a shipping quote"
                  : "We will email you a receipt"}
                )
              </Typography>
            </>
          )}

          <Typography variant="h6">Recipient Information</Typography>
          <Typography color="text.secondary" variant="subtitle1">
            (
            {[
              DELIVERY_OPTIONS.US_MAINLAND_SHIPPING,
              DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING,
            ].includes(deliveryOption)
              ? "Shipping"
              : "Delivery"}{" "}
            Information)
          </Typography>

          <Grid container spacing={2} mt={0} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextInput
                name="firstName"
                control={control}
                rules={{
                  required: {
                    message: "Please enter recipient's first name.",
                    value: true,
                  },
                }}
                label="Recipient's First Name"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                name="lastName"
                control={control}
                rules={{
                  required: {
                    message: "Please enter recipient's last name.",
                    value: true,
                  },
                }}
                label="Recipient's Last Name"
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>

          <TextInput
            name="address1"
            control={control}
            rules={{
              required: {
                message: "Please enter recipient's street address.",
                value: true,
              },
            }}
            label="Recipient's Street Address"
            size="small"
            fullWidth
            disabled={isAsrc}
            sx={{ mb: 2 }}
          />

          <TextInput
            name="address2"
            control={control}
            label="Recipient's Apt # or Hotel Name & Room #"
            size="small"
            fullWidth
            disabled={isAsrc}
            sx={{ mb: 2 }}
          />

          <Tooltip
            title="City was autofilled from the option that you chose from the previous page. Go back to the options page to update it."
            arrow
          >
            <TextInput
              name="city"
              control={control}
              label="Recipient's City"
              size="small"
              fullWidth
              disabled
              sx={{ mb: 2 }}
            />
          </Tooltip>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <Tooltip
                title="State was autofilled from the option that you chose from the previous page. Go back to the options page to update it."
                arrow
              >
                <TextInput
                  name="province"
                  control={control}
                  label="Recipient's State"
                  size="small"
                  fullWidth
                  disabled
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Tooltip
                title="Country was autofilled from the option that you chose from the previous page. Go back to the options page to update it."
                arrow
              >
                <TextInput
                  name="country"
                  control={control}
                  label="Recipient's Country"
                  size="small"
                  fullWidth
                  disabled
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Tooltip
                title="ZIP Code was autofilled from the option that you chose from the previous page. Go back to the options page to update it."
                arrow
              >
                <TextInput
                  name="zip"
                  control={control}
                  label="Recipient's ZIP Code"
                  size="small"
                  fullWidth
                  disabled
                />
              </Tooltip>
            </Grid>
          </Grid>

          <PhoneInput
            control={control}
            label="Recipient's Phone Number"
            size="small"
            fullWidth
            sx={{ mb: 0.5 }}
          />
        </Grid>
      </Grid>

      {[DELIVERY_OPTIONS.US_MAINLAND_SHIPPING, DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING].includes(deliveryOption) && hasBeerItems ? (
        <BackAndNextButton
          // backButtonLabel="Return to options"
          backButtonPath="../options"
          NextButtonComponent={() => (
            <LoadingButton
              loading={isLoading || isDraftOrderLoading}
              type="submit"
              variant="contained"
              size="large"
              sx={{ fontWeight: 500, height: 60, textTransform: "none" }}
            >
              Submit
            </LoadingButton>
          )}
        />
      ) : (
        <>
          <BottomNav loading={isLoading} deliveryOption={deliveryOption} />

          {deliveryOption !== DELIVERY_OPTIONS.US_MAINLAND_SHIPPING && <>
            <Addons />

            <BottomNav loading={isLoading} deliveryOption={deliveryOption} />
          </>}
        </>
      )}

      <Dialog
        fullScreen={fullScreen}
        maxWidth="md"
        open={Boolean(draftOrderData?.id)}
      >
        <DialogTitle textAlign="center">
          Thank you! Your form has been submitted.
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body1" gutterBottom>
            Our team will email you shortly with a shipping qoute.
          </Typography>

          <Button
            variant="contained"
            href="/"
            sx={{
              mt: 2,
            }}
          >
            Go Back to Homepage
          </Button>
        </DialogContent>
      </Dialog>
    </form>
  )
}
